import React from 'react';
import { DefaultBasePage, SurveysEmptyState } from '@components';
import { useNavigate } from 'react-router-dom';
import { Skeleton, Stack, useTheme, Box } from '@mui/material';
import {
  SurveyInstance,
  useGetAvailableSurveysQuery,
  useListAnytimeSurveyAssignmentsQuery,
  AssignmentType,
  SurveyStatus,
} from '@fdha/graphql-api-patient';
import { List, Typography } from '@fdha/web-ui-library';
import { NetworkStatus } from '@apollo/client';
import { useAnalytics } from '@fdha/common-hooks';

import SurveyCard from './SurveyCard';

const Surveys = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { analyticsClient } = useAnalytics();

  const { data, networkStatus } = useGetAvailableSurveysQuery({
    variables: { showDataSurveys: true, version: 5 },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });

  const surveys = data?.availableSurveys.edges.map((edge) => edge.node) || [];

  const isLoading = networkStatus === NetworkStatus.loading;

  const {
    data: anytimeSurveysData,
    networkStatus: anytimeSurveysNetworkStatus,
  } = useListAnytimeSurveyAssignmentsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });
  const anytimeSurveys =
    (anytimeSurveysData?.anytimeSurveyAssignments.edges.map((edge) => ({
      ...edge.node,
      isAnytimeSurvey: true,
      due_at: '',
      status: SurveyStatus.Open,
    })) || []) as SurveyInstance[];

  const isLoadingAnytimeSurveys =
    anytimeSurveysNetworkStatus === NetworkStatus.loading;

  const renderLoadingState = () => (
    <Stack spacing={2}>
      {[...Array(7)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" height={96} />
      ))}
    </Stack>
  );

  const navigateToSurvey = (item: SurveyInstance) => {
    const isAnytimeSurvey = 'isAnytimeSurvey' in item;
    navigate(item.id, {
      state: {
        isAnytimeSurvey,
        surveyData: isAnytimeSurvey ? item : undefined,
      },
    });
    analyticsClient?.track('Survey Opened', {
      id: item.id,
      name: item.name,
      type: 'ANYTIME',
    });
  };

  const renderItem = (item: SurveyInstance) => (
    <SurveyCard
      key={item.id}
      name={item.name}
      status={item.status || SurveyStatus.Open}
      frequency={item.schedule?.frequency}
      dueAt={item.due_at}
      weekDays={item.schedule?.weekDays || []}
      isAnytimeSurvey={item.isAnytimeSurvey || false}
      lastRespondedAt={item.lastRespondedAt}
      handleClick={() => navigateToSurvey(item)}
    />
  );

  const renderEmptyState = () => {
    return <SurveysEmptyState />;
  };

  const renderSurveyTypeTitle = (type: AssignmentType) => {
    const fallback =
      type === AssignmentType.Available ? 'Available' : 'Required';

    return (
      <Box mt={type === AssignmentType.Available ? 4 : 0} mb={2}>
        <Typography
          i18nKey={`surveys:screen.${type}`}
          variant="body2"
          color={theme.palette.text.secondary}
        >
          {fallback}
        </Typography>
      </Box>
    );
  };

  return (
    <DefaultBasePage
      i18nKeyTitle="surveys:title"
      title="Surveys"
      contentSize="small"
    >
      <Typography
        i18nKey="surveys:subtitle"
        color={theme.palette.text.secondary}
        mb={2}
      >
        This is how your Coach can stay in the loop, all information is
        confidential between the two of you.
      </Typography>
      {isLoading ? (
        renderLoadingState()
      ) : (
        <>
          {surveys.length > 0 && renderSurveyTypeTitle(AssignmentType.Required)}
          <List
            items={surveys}
            renderItem={renderItem}
            renderEmptyState={renderEmptyState}
            isLoading={isLoading}
          />
          {anytimeSurveys.length > 0 && (
            <>
              {renderSurveyTypeTitle(AssignmentType.Available)}
              <List
                items={anytimeSurveys}
                renderItem={renderItem}
                isLoading={isLoadingAnytimeSurveys}
              />
            </>
          )}
        </>
      )}
    </DefaultBasePage>
  );
};

export default Surveys;
